import type { GoodLuckCoreDTOsChatAttachmentDto as AttachmentDto } from "@/shared/api/generated-kubb/types/goodLuck/core/DTOs/ChatAttachmentDto";
import type { GoodLuckCoreDTOsChatListDto as ChatListDto } from "@/shared/api/generated-kubb/types/goodLuck/core/DTOs/ChatListDto";
import type { GoodLuckCoreDTOsChatMediaCountsDto as MediaCountsDto } from "@/shared/api/generated-kubb/types/goodLuck/core/DTOs/ChatMediaCountsDto";
import type { GoodLuckCoreDTOsChatMediaItemDto as MediaItemDto } from "@/shared/api/generated-kubb/types/goodLuck/core/DTOs/ChatMediaItemDto";
import type { GoodLuckCoreDTOsChatMessageDto as MessageDto } from "@/shared/api/generated-kubb/types/goodLuck/core/DTOs/ChatMessageDto";
import type { GoodLuckCoreDTOsChatMessagesPageDto as MessagesPageDto } from "@/shared/api/generated-kubb/types/goodLuck/core/DTOs/ChatMessagesPageDto";
import type { GoodLuckCoreDTOsChatPinDto as PinDto } from "@/shared/api/generated-kubb/types/goodLuck/core/DTOs/ChatPinDto";
import type { GoodLuckCoreDTOsChatReactionDto as ReactionDto } from "@/shared/api/generated-kubb/types/goodLuck/core/DTOs/ChatReactionDto";
import type { GoodLuckCoreDTOsConversationMemberDto as MemberDto } from "@/shared/api/generated-kubb/types/goodLuck/core/DTOs/ConversationMemberDto";
import type { GoodLuckCoreDTOsConversationSummaryDto as SummaryDto } from "@/shared/api/generated-kubb/types/goodLuck/core/DTOs/ConversationSummaryDto";
import type { GoodLuckCoreDTOsLfgCardDto as LfgCardDto } from "@/shared/api/generated-kubb/types/goodLuck/core/DTOs/LfgCardDto";
import type { GoodLuckCoreDTOsScrimOptionsDto as ScrimOptionsDto } from "@/shared/api/generated-kubb/types/goodLuck/core/DTOs/ScrimOptionsDto";
import type { GoodLuckCoreDTOsScrimTeamOptionDto as ScrimTeamDto } from "@/shared/api/generated-kubb/types/goodLuck/core/DTOs/ScrimTeamOptionDto";
import {
  ATTACHMENT_TYPES,
  CONVERSATION_TYPES,
  MESSAGE_KINDS,
  type AttachmentType,
  type ChatAttachment,
  type ChatList,
  type LfgCard,
  type ScrimCard,
  type ScrimOptions,
  type ScrimTeamOption,
  type ChatMessage,
  type ChatMessagesPage,
  type ChatMediaCounts,
  type ChatMediaItem,
  type ChatMediaKind,
  type ChatPin,
  type ChatReaction,
  type Conversation,
  type ConversationMember,
  type ConversationType,
  type MessageKind,
} from "../model/types";

// KAN-1342/1477: единственная точка разбора ответов /conversations. Поля читаются через типы, сгенерированные
// из спеки бэкенда (Kubb, make api-sync-test): переименование на бэке ломает сборку, а не даёт тихую пустоту.
// Спека помечает всё как необязательное, поэтому обязательное проверяем здесь: невалидный ответ бросает —
// React Query покажет ошибку, а не полуобъект (ai/frontend-standard.md §2.3).

/** Граница сети: ответ — объект нужного DTO (поля проверяются ниже, по месту). */
function dto<T>(value: unknown, what: string): T {
  if (typeof value !== "object" || value === null || Array.isArray(value)) throw new Error(`Invalid ${what}: not an object`);
  return value as T;
}

/** Обязательная непустая строка. */
function need(value: string | null | undefined, what: string, key: string): string {
  if (!value) throw new Error(`Invalid ${what}: ${key} missing`);
  return value;
}

const list = <T>(value: T[] | null | undefined): T[] => value ?? [];

function oneOf<T extends string>(all: readonly T[], raw: unknown, what: string): T {
  const hit = all.find((x) => x === raw);
  if (!hit) throw new Error(`Unknown ${what}: ${String(raw)}`);
  return hit;
}

export const parseConversationType = (raw: unknown): ConversationType => oneOf(CONVERSATION_TYPES, raw, "conversation type");
export const parseMessageKind = (raw: unknown): MessageKind => oneOf(MESSAGE_KINDS, raw, "message kind");
export const parseAttachmentType = (raw: unknown): AttachmentType => oneOf(ATTACHMENT_TYPES, raw, "attachment type");

/** KAN-1603: «Личное» (личка с самим собой) рисуется закладкой везде, где есть аватар беседы, — не своим фото. */
export const SAVED_AVATAR = "gl:saved";

export function toConversation(raw: unknown): Conversation {
  const o = dto<SummaryDto>(raw, "conversation");
  const last = o.lastMessage;
  const type = parseConversationType(o.type);
  return {
    id: need(o.id, "conversation", "id"),
    type,
    title: o.title ?? "",
    avatarUrl: type === "direct" && o.memberCount === 1 ? SAVED_AVATAR : (o.avatarUrl ?? null),
    peerUserId: o.peerUserId ?? null,
    memberCount: o.memberCount ?? 0,
    lastMessage: last
      ? {
          id: need(last.id, "last message", "id"),
          senderId: last.senderId ?? null,
          senderDisplayName: last.senderDisplayName ?? null,
          preview: last.preview ?? "",
          sentAtUtc: need(last.sentAtUtc, "last message", "sentAtUtc"),
          isDeleted: last.isDeleted === true,
        }
      : null,
    unreadCount: o.unreadCount ?? 0,
    isArchived: o.isArchived === true,
    notificationsMuted: o.notificationsMuted === true,
    pinnedAtUtc: o.pinnedAtUtc ?? null,
    chatListId: o.chatListId ?? null,
    isOwner: o.isOwner === true,
    blockedByMe: o.blockedByMe === true,
    blockedMe: o.blockedMe === true,
    membersCanInvite: o.membersCanInvite === true,
    mediaAllowed: o.mediaAllowed === true,
    slowMode: o.slowMode === true,
    expiresAtUtc: o.expiresAtUtc ?? null,
  };
}

function toAttachment(o: AttachmentDto): ChatAttachment {
  return {
    id: need(o.id, "attachment", "id"),
    type: parseAttachmentType(o.type),
    url: need(o.url, "attachment", "url"),
    thumbnailUrl: o.thumbnailUrl ?? null,
    fileName: o.fileName ?? null,
    sizeBytes: o.sizeBytes ?? null,
    width: o.width ?? null,
    height: o.height ?? null,
    durationSec: o.durationSec ?? null,
  };
}

/** Реакции из ответа API или события хаба (ReactionsChanged). */
export function toReactions(raw: unknown): ChatReaction[] {
  if (!Array.isArray(raw)) return [];
  return (raw as ReactionDto[]).map((o) => ({
    emoji: need(o.emoji, "reaction", "emoji"),
    count: o.count ?? 0,
    userIds: list(o.userIds),
    names: list(o.names),
    avatars: list(o.avatars).map((a) => a ?? null),
    myAtUtc: o.myAtUtc ?? null,
  }));
}

const MEDIA_KINDS: readonly ChatMediaKind[] = ["photo", "video", "file", "voice", "round", "link"];

export function toMediaCounts(raw: unknown): ChatMediaCounts {
  const o = raw && typeof raw === "object" ? (raw as MediaCountsDto) : {};
  return { photos: o.photos ?? 0, videos: o.videos ?? 0, files: o.files ?? 0, voices: o.voices ?? 0, links: o.links ?? 0 };
}

export function toMediaItems(raw: unknown): ChatMediaItem[] {
  if (!Array.isArray(raw)) return [];
  return (raw as MediaItemDto[]).flatMap((o): ChatMediaItem[] => {
    const kind = MEDIA_KINDS.find((k) => k === o.kind);
    if (!kind) return [];
    return [
      {
        messageId: need(o.messageId, "media item", "messageId"),
        attachmentId: o.attachmentId ?? null,
        kind,
        url: need(o.url, "media item", "url"),
        thumbnailUrl: o.thumbnailUrl ?? null,
        fileName: o.fileName ?? null,
        sizeBytes: o.sizeBytes ?? null,
        width: o.width ?? null,
        height: o.height ?? null,
        durationSec: o.durationSec ?? null,
        title: o.title ?? null,
        senderDisplayName: o.senderDisplayName ?? null,
        sentAtUtc: need(o.sentAtUtc, "media item", "sentAtUtc"),
      },
    ];
  });
}

export function toChatPins(raw: unknown): ChatPin[] {
  if (!Array.isArray(raw)) return [];
  return (raw as PinDto[]).map((o) => ({
    messageId: need(o.messageId, "pin", "messageId"),
    senderDisplayName: o.senderDisplayName ?? null,
    preview: o.preview ?? "",
    pinnedAtUtc: need(o.pinnedAtUtc, "pin", "pinnedAtUtc"),
  }));
}

export function toChatMessage(raw: unknown): ChatMessage {
  const o = dto<MessageDto>(raw, "message");
  const reply = o.replyTo;
  return {
    id: need(o.id, "message", "id"),
    conversationId: need(o.conversationId, "message", "conversationId"),
    senderId: o.senderId ?? null,
    senderDisplayName: o.senderDisplayName ?? null,
    senderAvatarUrl: o.senderAvatarUrl ?? null,
    kind: parseMessageKind(o.kind),
    body: o.body ?? "",
    payloadJson: o.payloadJson ?? null,
    attachments: list(o.attachments).map(toAttachment),
    sentAtUtc: need(o.sentAtUtc, "message", "sentAtUtc"),
    editedAtUtc: o.editedAtUtc ?? null,
    replyTo: reply
      ? {
          id: need(reply.id, "reply", "id"),
          senderDisplayName: reply.senderDisplayName ?? null,
          preview: reply.preview ?? null,
          isDeleted: reply.isDeleted === true,
          senderAvatarUrl: reply.senderAvatarUrl ?? null,
        }
      : null,
    forwardedFromName: o.forwardedFromName ?? null,
    isDeleted: o.isDeleted === true,
    isDelivered: o.isDelivered === true,
    isRead: o.isRead === true,
    reactions: toReactions(o.reactions),
    clientKey: o.clientKey ?? null,
  };
}

export function toChatMessagesPage(raw: unknown): ChatMessagesPage {
  const o = dto<MessagesPageDto>(raw, "messages page");
  if (!Array.isArray(o.messages)) throw new Error("Invalid messages page: messages missing");
  return { messages: o.messages.map(toChatMessage), hasMore: o.hasMore === true };
}

export function toConversationMember(raw: unknown): ConversationMember {
  const o = dto<MemberDto>(raw, "member");
  return {
    userId: need(o.userId, "member", "userId"),
    // KAN-1578: поля ещё нет в сгенерированном типе (до api-sync) — читаем явно.
    username: (o as MemberDto & { username?: string | null }).username ?? null,
    displayName: o.displayName ?? null,
    avatarUrl: o.avatarUrl ?? null,
    joinedAtUtc: need(o.joinedAtUtc, "member", "joinedAtUtc"),
    isOwner: o.isOwner === true,
    mutedUntilUtc: o.mutedUntilUtc ?? null,
  };
}

export function toChatList(raw: unknown): ChatList {
  const o = dto<ChatListDto>(raw, "chat list");
  return { id: need(o.id, "chat list", "id"), kind: oneOf(["direct", "groups"] as const, o.kind, "chat list kind"), title: need(o.title, "chat list", "title") };
}

export const toChatLists = (dto: unknown): ChatList[] => (Array.isArray(dto) ? dto.map(toChatList) : []);

const strArr = (v: unknown): string[] => (Array.isArray(v) ? v.filter((x): x is string => typeof x === "string") : []);

/** Карточка из payloadJson; битый или чужой формат — null, сообщение рисуется без карточки. */
export function parseLfgCard(payloadJson: string | null): LfgCard | null {
  if (!payloadJson) return null;
  try {
    const o = JSON.parse(payloadJson) as Record<string, unknown>;
    if (typeof o["postId"] !== "string" || typeof o["authorId"] !== "string") return null;
    return {
      postId: o["postId"],
      type: o["type"] === "lft" ? "lft" : "lfg",
      authorId: o["authorId"],
      authorName: typeof o["authorName"] === "string" ? o["authorName"] : "",
      authorAvatarUrl: typeof o["authorAvatarUrl"] === "string" ? o["authorAvatarUrl"] : null,
      teamName: typeof o["teamName"] === "string" ? o["teamName"] : null,
      role: typeof o["role"] === "string" ? o["role"] : "",
      roles: strArr(o["roles"]),
      formats: strArr(o["formats"]),
      minRank: typeof o["minRank"] === "string" ? o["minRank"] : null,
      maxRank: typeof o["maxRank"] === "string" ? o["maxRank"] : null,
      description: typeof o["description"] === "string" ? o["description"] : "",
    };
  } catch {
    return null;
  }
}

/** Свои объявления для прикрепления (GET /conversations/lfg-cards) — те же поля, что у карточки в ленте. */
export function toLfgCards(raw: unknown): LfgCard[] {
  if (!Array.isArray(raw)) return [];
  return (raw as LfgCardDto[]).flatMap((o): LfgCard[] =>
    o.postId && o.authorId
      ? [
          {
            postId: o.postId,
            type: o.type === "lft" ? "lft" : "lfg",
            authorId: o.authorId,
            authorName: o.authorName ?? "",
            authorAvatarUrl: o.authorAvatarUrl ?? null,
            teamName: o.teamName ?? null,
            role: o.role ?? "",
            roles: list(o.roles),
            formats: list(o.formats),
            minRank: o.minRank ?? null,
            maxRank: o.maxRank ?? null,
            description: o.description ?? "",
          },
        ]
      : [],
  );
}

function toScrimTeam(v: unknown): ScrimTeamOption | null {
  if (typeof v !== "object" || v === null) return null;
  const o = v as Record<string, unknown>;
  if (typeof o["id"] !== "string" || typeof o["name"] !== "string") return null;
  return { id: o["id"], name: o["name"], tag: typeof o["tag"] === "string" ? o["tag"] : "", logoUrl: typeof o["logoUrl"] === "string" ? o["logoUrl"] : null };
}

export function toScrimOptions(raw: unknown): ScrimOptions {
  const o = raw && typeof raw === "object" ? (raw as ScrimOptionsDto) : {};
  const teams = (v: ScrimTeamDto[] | null | undefined) => list(v).map(toScrimTeam).filter((t): t is ScrimTeamOption => t !== null);
  return { myTeams: teams(o.myTeams), peerTeams: teams(o.peerTeams) };
}

/** Карточка скрима из payloadJson; битый формат — null. */
export function parseScrimCard(payloadJson: string | null): ScrimCard | null {
  if (!payloadJson) return null;
  try {
    const o = JSON.parse(payloadJson) as Record<string, unknown>;
    const from = toScrimTeam(o["from"]);
    const to = toScrimTeam(o["to"]);
    if (typeof o["scrimId"] !== "string" || !from || !to) return null;
    const at = o["scheduledAt"];
    const bo = o["bestOf"];
    const scheduledAt = typeof at === "string" ? at : null;
    const dates = Array.isArray(o["dates"]) ? o["dates"].filter((x): x is string => typeof x === "string") : [];
    return { scrimId: o["scrimId"], from, to, scheduledAt, bestOf: typeof bo === "number" ? bo : null, dates: dates.length > 0 ? dates : scheduledAt ? [scheduledAt] : [] };
  } catch {
    return null;
  }
}
